import { ApiError } from '../core/api/http'
import { isIsoDate, nowStamp, today } from '../core/format/dates'
import { applyMovement, movementProblem, movementsCsv, movementsFor, withSold30 } from '../core/rules/stockMovements'
import { localCatalog } from '../data/catalog'
import { db, type StockItemRow, type StockMovementRow } from './db'
import { productOf } from './pricing'
import { demoId, reply } from './reply'
import { demoAdminIdentity } from './adminIdentity'
import type { AdminBackend } from '../core/types/backend'
import type { StockItem, StockLevels, StockLocation, StockMovement } from '../core/types/stock'

/**
 * DEMO — voorraad per plek aan de kant van de server (docs/VOORRAAD.md). De webshopvoorraad van
 * een catalogusproduct is de producttabel (`db.stockOverrides`, dezelfde kolom als `Product.stock`);
 * salonvoorraad, inkoopprijs en verbruiksartikelen staan in `stockItems`. Elke verandering is een
 * regel in `stockMovements`, in dezelfde "transactie" als de stand.
 */

export const rowOf = (id: string): StockItemRow | undefined => db.stockItems().find((r) => r.id === id)

/**
 * Een collega met alleen kassa- of voorraadrechten krijgt geen inkoopprijzen (LARAVEL.md §9.8):
 * alleen met `stock.cost` of `finance.margin`.
 */
const seesCost = () => {
  const g = demoAdminIdentity.access().grants
  return g['stock.cost'] === 'all' || g['finance.margin'] === 'all'
}
function withoutCostUnlessAllowed<T extends { costPriceCents?: number }>(x: T): T {
  if (seesCost() || x.costPriceCents === undefined) return x
  const { costPriceCents: _c, ...rest } = x
  return rest as T
}

/** Producten die voorraad kunnen hebben: geen cadeaubonnen of kuren. */
const retailIds = () => localCatalog.products.filter((p) => p.kind !== 'giftcard' && p.kind !== 'kuur').map((p) => p.id)

function webshopLevel(id: string, row: StockItemRow | undefined): number | null {
  if (row?.kind === 'verbruik') return row.webshop ?? null
  const overrides = db.stockOverrides()
  if (id in overrides) return overrides[id]
  const stock = productOf(id)?.stock
  return typeof stock === 'number' ? stock : null
}

export function levelsOf(id: string): StockLevels {
  const row = rowOf(id)
  return { webshop: webshopLevel(id, row), salon: row?.salon ?? null }
}

function setLevels(id: string, levels: StockLevels) {
  const row = rowOf(id) ?? { id, kind: 'retail' as const, salon: null }
  if (row.kind === 'verbruik') db.putStockItem({ ...row, webshop: levels.webshop, salon: levels.salon })
  else {
    if (levels.webshop !== webshopLevel(id, row)) db.setStock(id, levels.webshop)
    db.putStockItem({ ...row, salon: levels.salon })
  }
}

export function toItem(id: string): StockItem | null {
  const row = rowOf(id)
  const product = row?.kind === 'verbruik' ? null : productOf(id)
  if (row?.kind !== 'verbruik' && !product) return null
  return withSold30(
    {
      id,
      kind: row?.kind ?? 'retail',
      name: row?.kind === 'verbruik' ? (row.name ?? id) : (product?.name ?? id),
      ...(row?.brand || product?.brand ? { brand: row?.brand ?? product?.brand } : {}),
      levels: levelsOf(id),
      ...(row?.costPriceCents !== undefined ? { costPriceCents: row.costPriceCents } : {}),
    },
    db.stockMovements(),
    today(),
  )
}

/**
 * Een mutatie boeken en de stand bijwerken, in één keer. Gebruikt door bestellingen (webshop), de
 * kassa (salon) en de knoppen in Voorraad. `qty` met teken: − = eraf.
 */
export function bookStock(
  itemId: string,
  m: Omit<StockMovement, 'id' | 'at' | 'by' | 'itemId' | 'costPriceCents' | 'accountId'> & { by?: string },
  requestId?: string,
): StockMovementRow {
  const row: StockMovementRow = {
    ...m,
    id: demoId('mut').toLowerCase(),
    itemId,
    at: nowStamp(),
    // Wie ingelogd is in het beheer; een bestelling uit de webshop zegt zelf "Webshop".
    ...(m.by ? { by: m.by } : demoAdminIdentity.actor()),
    ...(rowOf(itemId)?.costPriceCents !== undefined ? { costPriceCents: rowOf(itemId)?.costPriceCents } : {}),
    ...(requestId ? { requestId } : {}),
  }
  setLevels(itemId, applyMovement(levelsOf(itemId), row))
  db.addStockMovements([row])
  return row
}

/**
 * Productregels van een verkoop af- of terugboeken. Alleen producten; cadeaubonnen en kuren
 * hebben geen voorraad. Een plek die niet bijgehouden wordt (`null`) krijgt geen regel.
 */
export function bookSaleStock(
  lines: { productId: string; qty: number }[],
  location: StockLocation,
  sign: 1 | -1,
  ref: string,
  note?: string,
): void {
  lines.forEach((l) => {
    const p = productOf(l.productId)
    if (!p || p.kind === 'giftcard' || p.kind === 'kuur' || l.qty <= 0) return
    if (levelsOf(l.productId)[location] === null) return
    bookStock(
      l.productId,
      {
        reason: location === 'salon' ? 'verkoop-salon' : 'verkoop-webshop',
        location,
        qty: sign * l.qty,
        ref,
        ...(location === 'webshop' && sign === -1 ? { by: 'Webshop' } : {}),
        ...(note ? { note } : {}),
      },
    )
  })
}

export const fail = (message: string, status: number, code?: string, field?: string): never => {
  throw new ApiError(message, status, field ? { [field]: message } : undefined, code)
}

/** Laatste volledige telling per plek ("YYYY-MM-DDTHH:mm"), of `null`. */
const lastCountAt = (): Record<StockLocation, string | null> => {
  const last = (l: StockLocation) =>
    db
      .stockCounts()
      .filter((c) => c.location === l && c.complete)
      .map((c) => c.at)
      .sort()
      .pop() ?? null
  return { webshop: last('webshop'), salon: last('salon') }
}

export const itemIds = () => [...new Set([...retailIds(), ...db.stockItems().map((r) => r.id)])]

const nameOf = (id: string) => rowOf(id)?.name ?? productOf(id)?.name ?? id

export const demoStockBackend: Pick<
  AdminBackend,
  'fetchStock' | 'fetchStockMovements' | 'addStockMovement' | 'addStockItem' | 'patchStockItem' | 'exportStockMovements'
> = {
  fetchStock() {
    const items = itemIds()
      .map(toItem)
      .filter((i): i is StockItem => i !== null)
      .sort((a, b) => (a.kind === b.kind ? a.name.localeCompare(b.name, 'nl') : a.kind === 'retail' ? -1 : 1))
      .map(withoutCostUnlessAllowed)
    return reply({ items, lastCountAt: lastCountAt() }, 300)
  },

  fetchStockMovements(itemId) {
    const list = db
      .stockMovements()
      .filter((m) => m.itemId === itemId)
      .sort((a, b) => b.at.localeCompare(a.at))
      .map(({ requestId: _r, ...m }) => m)
      .map(withoutCostUnlessAllowed)
    return reply(list, 250)
  },

  async addStockMovement(req) {
    const item = toItem(req.itemId)
    if (!item) return fail('Dit artikel bestaat niet (meer).', 404)
    // Al geboekt met dit requestId: hetzelfde antwoord, niets dubbel.
    if (db.stockMovements().some((m) => m.requestId === req.requestId)) return reply(item, 250)
    const levels = levelsOf(req.itemId)
    const problem = movementProblem(req, levels)
    if (problem) {
      await reply(null, 250)
      return fail(problem, 422, 'invalid', req.type === 'telling' ? 'counted' : 'qty')
    }
    if (req.type === 'telling' && req.expected !== levels[req.location]) {
      await reply(null, 250)
      return fail(`Er is intussen iets veranderd: de stand is nu ${levels[req.location] ?? 'niet bijgehouden'}. Tel opnieuw of bevestig.`, 409, 'stock_changed')
    }
    if (req.type === 'inkoop' && req.costPriceCents !== undefined) {
      const row = rowOf(req.itemId) ?? { id: req.itemId, kind: 'retail' as const, salon: null }
      db.putStockItem({ ...row, costPriceCents: req.costPriceCents })
    }
    const transferId = req.type === 'overboeken' ? demoId('ov').toLowerCase() : undefined
    movementsFor(req, levels).forEach((m, i) =>
      bookStock(req.itemId, { ...m, ...(transferId ? { transferId } : {}) }, i === 0 ? req.requestId : undefined),
    )
    return reply(toItem(req.itemId) ?? item, 350)
  },

  async addStockItem(req) {
    const known = db.stockItems().find((r) => r.requestId === req.requestId)
    if (known) return reply(toItem(known.id), 250).then((i) => i ?? fail('Bestaat niet meer.', 404))
    const name = req.name.trim()
    if (!name) return fail('Geef het artikel een naam.', 422, 'invalid', 'name')
    const id = `verbruik-${demoId('').toLowerCase().replace(/[^a-z0-9]/g, '')}`
    db.putStockItem({
      id,
      kind: 'verbruik',
      name,
      ...(req.brand?.trim() ? { brand: req.brand.trim() } : {}),
      salon: 0,
      webshop: null,
      ...(req.costPriceCents !== undefined ? { costPriceCents: req.costPriceCents } : {}),
      requestId: req.requestId,
    })
    const item = toItem(id)
    return item ? reply(item, 350) : fail('Toevoegen lukte niet.', 500)
  },

  async patchStockItem(id, patch) {
    if (!toItem(id)) return fail('Dit artikel bestaat niet (meer).', 404)
    if (patch.costPriceCents !== null && (!Number.isInteger(patch.costPriceCents) || patch.costPriceCents < 0)) {
      return fail('Vul een geldige inkoopprijs in.', 422, 'invalid', 'costPriceCents')
    }
    const row = rowOf(id) ?? { id, kind: 'retail' as const, salon: null }
    const { costPriceCents: _old, ...rest } = row
    db.putStockItem(patch.costPriceCents === null ? rest : { ...rest, costPriceCents: patch.costPriceCents })
    const item = toItem(id)
    return item ? reply(item, 300) : fail('Bestaat niet meer.', 404)
  },

  async exportStockMovements(from, to) {
    if (!isIsoDate(from) || !isIsoDate(to) || from > to) return fail('Kies een geldige periode.', 422)
    return reply(movementsCsv(db.stockMovements(), nameOf, from, to), 400)
  },
}
